import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Paper } from "@mui/material";
import { Search, ChevronLeft, ChevronRight, Eye, X, ChevronDown, Filter } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";

// Configure Axios Client
const api = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Live API is now integrated for FDP booking details modal.

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const DraggablePaper = (props) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [rel, setRel] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    const target = e.target;
    if (e.button !== 0 || !target.closest(".drag-handle")) return;

    setDragging(true);
    setRel({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
    e.stopPropagation();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragging) return;
      setPosition({
        x: e.clientX - rel.x,
        y: e.clientY - rel.y,
      });
      e.stopPropagation();
    };

    const handleMouseUp = () => {
      setDragging(false);
    };

    if (dragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, rel]);

  return (
    <Paper
      {...props}
      style={{
        ...props.style,
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: dragging ? "grabbing" : "auto",
        transition: dragging ? "none" : "transform 0.1s ease-out",
      }}
      onMouseDown={handleMouseDown}
    />
  );
};

const FdpView = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Debounce search term for API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Filter Dropdown States
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterSearchTerm, setFilterSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");

  const [isStateFilterOpen, setIsStateFilterOpen] = useState(false);
  const [stateFilterSearchTerm, setStateFilterSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedExpireDate, setSelectedExpireDate] = useState("");
  const [isExpireDateFilterOpen, setIsExpireDateFilterOpen] = useState(false);

  const [statesList, setStatesList] = useState([]);
  const [instituteTypesList, setInstituteTypesList] = useState([]);

  // API Integration States
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal Table States
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [modalItemsPerPage, setModalItemsPerPage] = useState(5);

  // Modal Live API States
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Fetch FDP history from server
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = {
          page: currentPage,
          page_size: itemsPerPage,
        };
        if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
          params.search = debouncedSearchTerm.trim();
        }
        if (selectedStateId) {
          params.state_id = selectedStateId;
        }
        if (selectedType) {
          params.institute_type = selectedType;
        }
        if (selectedExpireDate) {
          params.expire_date = selectedExpireDate;
        }
        const res = await api.get("/event/show_fdp_history", {
          params,
        });
        setData(res.data.data || []);
        setStatesList(res.data.states || []);
        setInstituteTypesList(res.data.institute_type || []);
        setTotalPages(res.data.total_pages || 0);
        setTotalRecords(res.data.total_records || 0);
      } catch (err) {
        console.error("Error fetching FDP history:", err);
        let errorMsg = "No FDP Found";
        if (err.response?.data?.detail) {
          const detail = err.response.data.detail;
          if (typeof detail === "string") {
            errorMsg = detail;
          } else if (Array.isArray(detail)) {
            errorMsg = detail.map((d) => d.msg || JSON.stringify(d)).join(", ");
          } else {
            errorMsg = JSON.stringify(detail);
          }
        } else if (err.message) {
          errorMsg = err.message;
        }
        setError(errorMsg);
        setData([]);
        setStatesList([]);
        setInstituteTypesList([]);
        setTotalPages(0);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, selectedStateId, selectedType, selectedExpireDate]);

  // Fetch FDP booking details when modal is opened
  useEffect(() => {
    if (!isModalOpen || !selectedItem) return;

    const fetchModalData = async () => {
      const instituteId = selectedItem.institute_id;
      if (instituteId === undefined || instituteId === null || isNaN(Number(instituteId))) {
        setModalError("Invalid Institute ID");
        return;
      }
      try {
        setModalLoading(true);
        setModalError(null);
        console.log("FdpView: Fetching details for instituteId:", instituteId);
        const res = await api.get(`/event/show_fdp_booking_details/${instituteId}`);
        console.log("FdpView: Received API response:", res.data);
        setModalData(res.data);
      } catch (err) {
        console.error("Error fetching FDP booking details:", err);
        let errorMsg = "Failed to load booking details";
        if (err.response?.data?.detail) {
          const detail = err.response.data.detail;
          if (typeof detail === "string") {
            errorMsg = detail;
          } else if (Array.isArray(detail)) {
            errorMsg = detail.map((d) => d.msg || JSON.stringify(d)).join(", ");
          } else {
            errorMsg = JSON.stringify(detail);
          }
        } else if (err.message) {
          errorMsg = err.message;
        }
        setModalError(errorMsg);
        setModalData(null);
      } finally {
        setModalLoading(false);
      }
    };

    fetchModalData();
  }, [isModalOpen, selectedItem]);

  // Modal close handler
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setModalSearchTerm("");
    setModalCurrentPage(1);
    setModalItemsPerPage(5);
    setModalData(null);
    setModalLoading(false);
    setModalError(null);
  };

  // Extract distinct institute types from loaded data
  const uniqueInstituteTypes = useMemo(() => {
    if (!instituteTypesList) return [];
    const types = instituteTypesList
      .map((item) => item.institute_type)
      .filter((type) => type !== undefined && type !== null && type !== "");
    return [...new Set(types)];
  }, [instituteTypesList]);

  // Filter the unique types based on inner dropdown search
  const filteredUniqueTypes = useMemo(() => {
    const search = filterSearchTerm.toLowerCase();
    return uniqueInstituteTypes.filter((type) =>
      type.toLowerCase().includes(search)
    );
  }, [uniqueInstituteTypes, filterSearchTerm]);

  // Extract state names and IDs from statesList
  const uniqueStates = useMemo(() => {
    return statesList.map((state) => ({
      id: state.state_id,
      name: state.state_name,
    }));
  }, [statesList]);

  // Filter the unique states based on inner dropdown search
  const filteredUniqueStates = useMemo(() => {
    const search = stateFilterSearchTerm.toLowerCase();
    return uniqueStates.filter((state) =>
      state.name.toLowerCase().includes(search)
    );
  }, [uniqueStates, stateFilterSearchTerm]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = data || [];

  // Modal table filtering & pagination logic
  const filteredModalDetails = useMemo(() => {
    if (!selectedItem || !modalData) {
      console.log("FdpView: selectedItem or modalData is missing", { selectedItem, modalData });
      return [];
    }

    console.log("FdpView: raw modalData:", modalData);

    // Map live API data
    const rawList = Array.isArray(modalData)
      ? modalData
      : (modalData.data && Array.isArray(modalData.data))
        ? modalData.data
        : [];

    console.log("FdpView: rawList after extraction:", rawList);

    const details = rawList.map((item) => {
      const isPaid = item.payment_status && item.payment_status.toLowerCase() === "paid";
      const isCsr = item.slot_type && String(item.slot_type).trim().toLowerCase() === "csr";
      const mappedSlotType = (isCsr || isPaid) ? "COE" : "complimentary";
      return {
        domainName: item.domain_name,
        studentsParticipated: item.participated_students,
        passedStudents: item.passed_students,
        paymentType: isPaid ? "Paid" : "Free",
        slotType: mappedSlotType,
        startDate: formatDate(item.start_date),
        endDate: formatDate(item.end_date),
      };
    });

    return details.filter((detail) => {
      const search = modalSearchTerm.toLowerCase();
      return (
        (detail.domainName && detail.domainName.toLowerCase().includes(search)) ||
        (detail.paymentType && detail.paymentType.toLowerCase().includes(search)) ||
        (detail.slotType && detail.slotType.toLowerCase().includes(search)) ||
        (detail.startDate && detail.startDate.toLowerCase().includes(search)) ||
        (detail.endDate && detail.endDate.toLowerCase().includes(search))
      );
    });
  }, [selectedItem, modalData, modalSearchTerm]);

  const modalTotalPages = Math.ceil(filteredModalDetails.length / modalItemsPerPage);
  const modalStartIndex = (modalCurrentPage - 1) * modalItemsPerPage;
  const currentModalItems = filteredModalDetails.slice(
    modalStartIndex,
    modalStartIndex + modalItemsPerPage
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-md border border-gray-200 p-6 space-y-6"
      >
      {/* Header and Search */}
      <div className="flex flex-col gap-3">
        <h3 className="text-xl font-bold text-gray-800">Detail View</h3>
        <div className="relative w-full sm:w-80">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm outline-none transition-all bg-white text-gray-900 placeholder-gray-400"
          />
        </div>
      </div>

      {/* Table with Sticky Header & Scrollable Body */}
      <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-310px)] min-h-[300px] rounded-lg border border-gray-100 relative">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gradient-to-r from-indigo-950 to-blue-600 text-white sticky top-0 z-20">
            <tr>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider w-20 align-middle">
                Sl. No.
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider align-middle">
                Institute
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider relative align-middle">
                <div className="flex items-center gap-2">
                  <span>Institute Type</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFilterOpen(!isFilterOpen);
                      setIsStateFilterOpen(false);
                      setIsExpireDateFilterOpen(false);
                    }}
                    className={`p-1 rounded-md transition-colors ${
                      selectedType
                        ? "text-yellow-300 hover:text-yellow-400 bg-white/10"
                        : "text-white/60 hover:text-white hover:bg-white/10"
                    }`}
                    title="Filter by Type"
                  >
                    <Filter className="w-3.5 h-3.5" />
                  </button>
                </div>

                {isFilterOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-30"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsFilterOpen(false);
                        setFilterSearchTerm("");
                      }}
                    />
                    <div className="absolute left-6 mt-2 w-60 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-40 focus:outline-none border border-gray-100 flex flex-col p-2 space-y-2 normal-case tracking-normal text-gray-700">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                          <Search className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          placeholder="Search type..."
                          value={filterSearchTerm}
                          onChange={(e) => setFilterSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="block w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs outline-none transition-all"
                        />
                      </div>

                      <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedType("");
                            setIsFilterOpen(false);
                            setFilterSearchTerm("");
                            setCurrentPage(1);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs rounded-lg transition font-medium ${
                            selectedType === ""
                              ? "bg-blue-50 text-blue-600"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          All Types
                        </button>
                        {filteredUniqueTypes.length > 0 ? (
                          filteredUniqueTypes.map((type) => (
                            <button
                              key={type}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedType(type);
                                setIsFilterOpen(false);
                                setFilterSearchTerm("");
                                setCurrentPage(1);
                              }}
                              className={`w-full text-left px-3 py-2 text-xs rounded-lg transition font-medium ${
                                selectedType === type
                                  ? "bg-blue-50 text-blue-600"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {type}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-xs text-gray-400 text-center select-none">
                            No types found
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider relative align-middle">
                <div className="flex items-center gap-2">
                  <span>State</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsStateFilterOpen(!isStateFilterOpen);
                      setIsFilterOpen(false);
                      setIsExpireDateFilterOpen(false);
                    }}
                    className={`p-1 rounded-md transition-colors ${
                      selectedStateId
                        ? "text-yellow-300 hover:text-yellow-400 bg-white/10"
                        : "text-white/60 hover:text-white hover:bg-white/10"
                    }`}
                    title="Filter by State"
                  >
                    <Filter className="w-3.5 h-3.5" />
                  </button>
                </div>

                {isStateFilterOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-30"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsStateFilterOpen(false);
                        setStateFilterSearchTerm("");
                      }}
                    />
                    <div className="absolute left-6 mt-2 w-60 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-40 focus:outline-none border border-gray-100 flex flex-col p-2 space-y-2 normal-case tracking-normal text-gray-700">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                          <Search className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          placeholder="Search state..."
                          value={stateFilterSearchTerm}
                          onChange={(e) => setStateFilterSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="block w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs outline-none transition-all"
                        />
                      </div>

                      <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedState("");
                            setSelectedStateId("");
                            setIsStateFilterOpen(false);
                            setStateFilterSearchTerm("");
                            setCurrentPage(1);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs rounded-lg transition font-medium ${
                            selectedState === ""
                              ? "bg-blue-50 text-blue-600"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          All States
                        </button>
                        {filteredUniqueStates.length > 0 ? (
                          filteredUniqueStates.map((state) => (
                            <button
                              key={state.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedState(state.name);
                                setSelectedStateId(state.id);
                                setIsStateFilterOpen(false);
                                setStateFilterSearchTerm("");
                                setCurrentPage(1);
                              }}
                              className={`w-full text-left px-3 py-2 text-xs rounded-lg transition font-medium ${
                                selectedState === state.name
                                  ? "bg-blue-50 text-blue-600"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {state.name}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-xs text-gray-400 text-center select-none">
                            No states found
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider relative align-middle">
                <div className="flex items-center gap-2">
                  <span>Expiry Date</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpireDateFilterOpen(!isExpireDateFilterOpen);
                      setIsFilterOpen(false);
                      setIsStateFilterOpen(false);
                    }}
                    className={`p-1 rounded-md transition-colors ${
                      selectedExpireDate
                        ? "text-yellow-300 hover:text-yellow-400 bg-white/10"
                        : "text-white/60 hover:text-white hover:bg-white/10"
                    }`}
                    title="Filter by Expiry Date"
                  >
                    <Filter className="w-3.5 h-3.5" />
                  </button>
                </div>

                {isExpireDateFilterOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-30"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsExpireDateFilterOpen(false);
                      }}
                    />
                    <div className="absolute left-6 mt-2 w-60 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-40 focus:outline-none border border-gray-100 flex flex-col p-3 space-y-3 normal-case tracking-normal text-gray-700">
                      <div className="text-xs font-semibold text-gray-600">Select Expiry Date:</div>
                      <input
                        type="date"
                        value={selectedExpireDate}
                        onChange={(e) => {
                          setSelectedExpireDate(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="block w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs outline-none transition-all text-gray-800"
                      />

                    </div>
                  </>
                )}
              </th>
              <th className="px-6 py-3.5 text-center text-xs font-semibold uppercase tracking-wider w-24 align-middle">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-10 text-center text-gray-500 align-middle">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Loading records...</span>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="6" className="px-6 py-10 text-center text-red-500 font-semibold align-middle">
                  {error}
                </td>
              </tr>
            ) : currentItems.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-10 text-center text-gray-500 align-middle">
                  No records found matching your search.
                </td>
              </tr>
            ) : (
              currentItems.map((item, idx) => (
                <tr 
                  key={item.institute_id || idx} 
                  className={`transition-colors hover:bg-blue-50/50 ${
                    idx % 2 === 0 ? "bg-white" : "bg-blue-50/30"
                  }`}
                >
                  <td className="px-6 py-4 font-semibold text-gray-800 w-20 align-middle whitespace-nowrap">
                    {item.sl_no || (startIndex + idx + 1)}
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-900 align-middle">
                    <span className="block truncate max-w-sm" title={item.institute_name}>
                      {item.institute_name}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600 align-middle whitespace-nowrap">
                    {item.institute_type}
                  </td>
                  <td className="px-6 py-4 text-gray-600 align-middle whitespace-nowrap">
                    {item.state_name}
                  </td>
                  <td className="px-6 py-4 text-gray-500 align-middle whitespace-nowrap">
                    {formatDate(item.expire_date)}
                  </td>
                  <td className="px-6 py-4 text-center align-middle whitespace-nowrap w-24">
                    <button
                      onClick={() => {
                        setSelectedItem(item);
                        setIsModalOpen(true);
                      }}
                      className="p-1.5 hover:bg-blue-50 text-gray-500 hover:text-blue-600 rounded-lg transition-all duration-200 inline-flex items-center justify-center"
                      title="View Details"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && !error && totalRecords > 0 && (
        <div className="flex justify-end items-center gap-6 pt-4 border-t border-gray-100 text-sm">
          {/* Rows per page selector */}
          <div className="flex items-center gap-2 text-slate-600">
            <span>Rows per page:</span>
            <div className="relative flex items-center">
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="appearance-none bg-transparent pr-5 pl-1 py-1 font-medium text-slate-800 outline-none cursor-pointer focus:ring-0 border-none"
              >
                {[5, 10, 20, 30, 50].map((val) => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </select>
              <svg
                className="w-4 h-4 text-slate-500 absolute right-0 pointer-events-none fill-current"
                viewBox="0 0 24 24"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
          </div>

          {/* Page records range */}
          <div className="text-slate-800 font-medium select-none">
            {startIndex + 1}-{Math.min(startIndex + itemsPerPage, totalRecords)} of {totalRecords}
          </div>

          {/* Navigation arrows */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-1 text-slate-600 hover:text-slate-900 disabled:text-slate-300 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-1 text-slate-600 hover:text-slate-900 disabled:text-slate-300 disabled:cursor-not-allowed transition"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}      </motion.div>

      {/* Details Modal */}
      {isModalOpen && selectedItem && (
        <Dialog
          open={isModalOpen}
          onClose={handleCloseModal}
          PaperComponent={DraggablePaper}
          maxWidth="lg"
          fullWidth
          disableEnforceFocus
          style={{ zIndex: 9999 }}
          PaperProps={{
            style: {
              maxHeight: "75vh",
            }
          }}
          sx={{
            "& .MuiDialog-container": {
              top: isDesktop ? "70px" : "54px",
              left: isDesktop ? "270px" : "0px",
              right: "0px",
              bottom: "0px",
              position: "fixed"
            },
            "& .MuiBackdrop-root": {
              display: "none"
            }
          }}
        >
          {/* Modal Header */}
          <DialogTitle className="drag-handle" style={{ cursor: "grab", padding: "16px 24px", borderBottom: "1px solid #f1f5f9" }}>
            <div className="flex justify-between items-center w-full select-none">
              <div>
                <h4 className="text-lg font-bold text-gray-800">{selectedItem.institute_name}</h4>
                <p className="text-xs text-gray-500 mt-0.5">FDP Participation & Results History</p>
              </div>
              <button 
                onClick={handleCloseModal}
                className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </DialogTitle>

          {/* Modal Content */}
          <DialogContent style={{ padding: "24px", overflowY: "auto" }}>
            <div className="space-y-6">
              {/* Limit Details Section */}
              {(() => {
                const educatorsLimit = modalData?.fdp_limits?.educators_limit;
                const hostLimit = modalData?.fdp_limits?.host_limit;
                return (
                  <div className="space-y-4 shrink-0">
                    {/* Educator Limit */}
                    <div>
                      <h5 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Educator Limit</h5>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Total Limit Card */}
                        <div className="flex flex-col p-3 rounded-xl bg-blue-50/50 border border-blue-100 text-center">
                          <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">Total Limit</span>
                          <span className="text-lg font-bold text-blue-900 mt-1">{educatorsLimit?.total || 0}</span>
                        </div>
                        {/* Used Card */}
                        <div className="flex flex-col p-3 rounded-xl bg-amber-50/50 border border-amber-100 text-center">
                          <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Used</span>
                          <span className="text-lg font-bold text-amber-900 mt-1">{educatorsLimit?.used || 0}</span>
                        </div>
                        {/* Available Card */}
                        <div className="flex flex-col p-3 rounded-xl bg-emerald-50/50 border border-emerald-100 text-center">
                          <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">Available</span>
                          <span className="text-lg font-bold text-emerald-900 mt-1">{educatorsLimit?.left || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* FDP Host Limit */}
                    {/* <div>
                      <h5 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">FDP Host Limit</h5>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="flex flex-col p-3 rounded-xl bg-purple-50/50 border border-purple-100 text-center">
                          <span className="text-[10px] font-semibold text-purple-600 uppercase tracking-wider">Total Limit</span>
                          <span className="text-lg font-bold text-purple-900 mt-1">{hostLimit?.total || 0}</span>
                        </div>
                        <div className="flex flex-col p-3 rounded-xl bg-orange-50/50 border border-orange-100 text-center">
                          <span className="text-[10px] font-semibold text-orange-600 uppercase tracking-wider">Used</span>
                          <span className="text-lg font-bold text-orange-900 mt-1">{hostLimit?.used || 0}</span>
                        </div>
                        <div className="flex flex-col p-3 rounded-xl bg-teal-50/50 border border-teal-100 text-center">
                          <span className="text-[10px] font-semibold text-teal-600 uppercase tracking-wider">Available</span>
                          <span className="text-lg font-bold text-teal-900 mt-1">{hostLimit?.left || 0}</span>
                        </div>
                      </div>
                    </div> */}
                  </div>
                );
              })()}

              {/* Modal Search Bar */}
              <div className="flex justify-start shrink-0">
                <div className="relative w-full sm:w-72">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search details..."
                    value={modalSearchTerm}
                    onChange={(e) => {
                      setModalSearchTerm(e.target.value);
                      setModalCurrentPage(1);
                    }}
                    className="block w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs outline-none transition-all"
                  />
                </div>
              </div>

              {/* Table Wrapper (Scrollable with Sticky Header) */}
              <div className="overflow-x-auto overflow-y-auto max-h-[38vh] rounded-lg border border-gray-100 relative">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gradient-to-r from-indigo-950 to-blue-600 text-white sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Domain Name</th>
                      <th className="px-4 py-3 text-center font-semibold uppercase tracking-wider">Participate</th>
                      <th className="px-4 py-3 text-center font-semibold uppercase tracking-wider">Passed</th>
                      <th className="px-4 py-3 text-center font-semibold uppercase tracking-wider">Payment Type</th>
                      <th className="px-4 py-3 text-center font-semibold uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-center font-semibold uppercase tracking-wider">Start Date</th>
                      <th className="px-4 py-3 text-center font-semibold uppercase tracking-wider">End Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {modalLoading ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-10 text-center text-gray-500">
                          <div className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span>Loading details...</span>
                          </div>
                        </td>
                      </tr>
                    ) : modalError ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-10 text-center text-red-500 font-semibold">
                          {modalError}
                        </td>
                      </tr>
                    ) : currentModalItems.length > 0 ? (
                      currentModalItems.map((detail, idx) => (
                        <tr 
                          key={idx} 
                          className={`hover:bg-blue-50/50 ${
                            idx % 2 === 0 ? "bg-white" : "bg-blue-50/30"
                          }`}
                        >
                          <td className="px-4 py-3.5 font-medium text-gray-900">{detail.domainName}</td>
                          <td className="px-4 py-3.5 text-center text-gray-600 font-semibold">{detail.studentsParticipated}</td>
                          <td className="px-4 py-3.5 text-center text-green-600 font-semibold">{detail.passedStudents}</td>
                          <td className="px-4 py-3.5 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${detail.paymentType === "Free" ? "bg-green-50 text-green-700 border-green-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                              {detail.paymentType}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-center text-gray-600 font-medium">
                            {detail.slotType || "-"}
                          </td>
                          <td className="px-4 py-3.5 text-center text-gray-500 whitespace-nowrap">{detail.startDate}</td>
                          <td className="px-4 py-3.5 text-center text-gray-500 whitespace-nowrap">{detail.endDate}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">No matching records found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Modal Table Pagination */}
              {filteredModalDetails.length > 0 && (
                <div className="flex justify-end items-center gap-6 pt-3 border-t border-gray-100 text-xs shrink-0 select-none">
                  {/* Rows per page selector */}
                  <div className="flex items-center gap-2 text-slate-600">
                    <span>Rows per page:</span>
                    <div className="relative flex items-center">
                      <select
                        value={modalItemsPerPage}
                        onChange={(e) => {
                          setModalItemsPerPage(Number(e.target.value));
                          setModalCurrentPage(1);
                        }}
                        className="appearance-none bg-transparent pr-5 pl-1 py-1 font-medium text-slate-800 outline-none cursor-pointer focus:ring-0 border-none"
                      >
                        {[5, 10, 20, 50].map((val) => (
                          <option key={val} value={val}>
                            {val}
                          </option>
                        ))}
                      </select>
                      <svg
                        className="w-3.5 h-3.5 text-slate-500 absolute right-0 pointer-events-none fill-current"
                        viewBox="0 0 24 24"
                      >
                        <path d="M7 10l5 5 5-5z" />
                      </svg>
                    </div>
                  </div>

                  {/* Page records range */}
                  <div className="text-slate-800 font-medium select-none">
                    {modalStartIndex + 1}-{Math.min(modalStartIndex + modalItemsPerPage, filteredModalDetails.length)} of {filteredModalDetails.length}
                  </div>

                  {/* Navigation arrows */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setModalCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={modalCurrentPage === 1}
                      className="p-1 text-slate-600 hover:text-slate-900 disabled:text-slate-300 disabled:cursor-not-allowed transition"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setModalCurrentPage((prev) => Math.min(prev + 1, modalTotalPages))}
                      disabled={modalCurrentPage === modalTotalPages}
                      className="p-1 text-slate-600 hover:text-slate-900 disabled:text-slate-300 disabled:cursor-not-allowed transition"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>

          {/* Modal Footer */}
          <DialogActions style={{ padding: "16px 24px", borderTop: "1px solid #f1f5f9", backgroundColor: "#f8fafc" }}>
            <button 
              onClick={handleCloseModal}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-semibold transition"
            >
              Close
            </button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default FdpView;
